import React, { useState } from 'react';
import { Gavel, SearchX } from 'lucide-react';
import DecisionModal from './DecisionModal';
import { DOSSIER_STATUSES_LABELS, DECISION_TYPES_LABELS } from '../../../../constants/dossierStatuses';
import { motion } from 'framer-motion';
import { useAuth } from '../../../../context/AuthProvider';
import Tooltip from '../../../../components/ui/Tooltip';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } }
};

export default function DossierDecision({ dossier, onDecisionSaved, externalModalState, setExternalModalState }) {
 const [internalModalOpen, setInternalModalOpen] = useState(false);
 const { user } = useAuth();

 const isDecideur = dossier.decideur?.userId === user?.id;
 const isOpen = externalModalState?.isOpen || internalModalOpen;
 const initialType = externalModalState?.initialType || null;

 const handleCloseModal = () => {
   if (externalModalState?.isOpen && setExternalModalState) {
     setExternalModalState({ isOpen: false, initialType: null });
   } else {
     setInternalModalOpen(false);
   }
 };

 const buttonContent = (
   <button 
     onClick={() => setInternalModalOpen(true)}
     className="btn btn-primary flex items-center w-full sm:w-auto justify-center"
   >
     {isDecideur ? "Rendre une décision" : "Ajouter une observation"}
   </button>
 );

 return (
 <div className="space-y-6">
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center workspace-card p-6 gap-4">
 <div>
 <h2 className="text-lg font-bold text-primary flex items-center">
 <Gavel className="w-5 h-5 mr-2 text-tertiary" />
 Comité et Décision
 </h2>
 <p className="text-sm text-tertiary mt-1">Formalisez la décision de faisabilité et passez l'opération à l'étape suivante.</p>
 </div>
 {buttonContent}
 </div>

 <div className="workspace-card p-6">
 <h3 className="text-md font-bold text-primary mb-6">Historique des Décisions</h3>
 {(!dossier.decisions || dossier.decisions.length === 0) ? (
 <div className="flex flex-col items-center justify-center py-10 bg-surface-muted rounded-xl border border-workspace border-dashed">
 <SearchX className="w-10 h-10 text-tertiary mb-3 opacity-50" />
 <p className="text-sm font-semibold text-primary">Aucune décision formelle</p>
 <p className="text-xs text-tertiary mt-1 text-center max-w-sm">Le comité ne s'est pas encore prononcé sur ce dossier. Cliquez sur le bouton ci-dessus pour enregistrer une première décision.</p>
 </div>
 ) : (
 <motion.ul 
 variants={containerVariants}
 initial="hidden"
 animate="visible"
 className="space-y-6"
 >
 {dossier.decisions.map(dec => (
 <motion.li variants={itemVariants} key={dec.id} className="border-l-4 border-secondary pl-4 py-1">
 <div className="flex justify-between items-start mb-2">
 <span className="font-semibold text-primary">{DECISION_TYPES_LABELS[dec.decision] || dec.decision}</span>
 <span className="text-xs text-tertiary">{new Date(dec.decidedAt).toLocaleDateString()} par {dec.decidedBy?.Nom || 'Inconnu'}</span>
 </div>
 {dec.comment && <p className="text-sm text-secondary mt-1"><strong className="text-primary">Commentaire:</strong> {dec.comment}</p>}
 {dec.conditions && <p className="text-sm text-secondary mt-1"><strong className="text-primary">Conditions:</strong> {dec.conditions}</p>}
 {dec.missingDocs && <p className="text-sm text-secondary mt-1"><strong className="text-primary">Pièces manquantes:</strong> {dec.missingDocs}</p>}
 {dec.approvedAmount && <p className="text-sm font-semibold text-success-600 mt-3">Montant validé: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(dec.approvedAmount)}</p>}
 </motion.li>
 ))}
 </motion.ul>
 )}
 </div>

 <DecisionModal 
   isOpen={isOpen} 
   onClose={handleCloseModal} 
   dossierId={dossier.id}
   initialType={initialType}
   isDecideur={isDecideur}
   onSuccess={() => {
   handleCloseModal();
 if(onDecisionSaved) onDecisionSaved();
 }}
 />
 </div>
 );
}
